import { useCallback, useEffect, useState } from "react";
import { Button, Spin, Empty, Tag, Popconfirm, Tooltip } from "antd";
import { NavLink } from "react-router-dom";
import { ReloadOutlined, DeleteOutlined, LinkOutlined } from "@ant-design/icons";
import { AffaireService } from "../../services/AffaireService";
import { catchError } from "../../services/DaoService";
import Toast from "../../helpers/Toast";
import { InlineEditableInput } from "../utils/InlineEditableInput";
import AffaireLienInlineCreator from "./AffaireLienInlineCreator";
import PropTypes from "prop-types";
import styles from "./AffaireLiensList.module.scss";

// Options de type proposées à l'édition inline, selon le sens dans lequel le
// lien est affiché (les labels diffèrent, le type de base stocké est le même).
const TYPE_OPTIONS_BY_DIRECTION = {
  sortant: [
    { value: "lie_a", label: "Lié à" },
    { value: "bloque", label: "Bloque" },
    { value: "duplique", label: "Duplique" },
    { value: "cause", label: "Cause" },
  ],
  entrant: [
    { value: "lie_a", label: "Lié à" },
    { value: "bloque", label: "Bloqué par" },
    { value: "duplique", label: "Dupliqué par" },
    { value: "cause", label: "Causé par" },
  ],
};

export default function AffaireLiensList({ affaire, canManage = false }) {
  const [loading, setLoading] = useState(true);
  const [liens, setLiens] = useState([]);

  const init = useCallback(() => {
    setLoading(true);
    AffaireService.getInstance()
      .getLiens(affaire.uid)
      .then(r => setLiens(r.data))
      .catch(catchError)
      .finally(() => setLoading(false));
  }, [affaire.uid]);

  useEffect(() => { init(); }, [init]);

  const handleTypeChange = (value, fieldName, lien) => {
    AffaireService.getInstance()
      .updateLien(lien.uid, { type: value })
      .then(r => {
        Toast.success(r.message);
        setLiens(prev => prev.map(l => (l.uid === lien.uid
          ? { ...l, type: value, label: TYPE_OPTIONS_BY_DIRECTION[l.direction].find(o => o.value === value)?.label }
          : l)));
      })
      .catch(catchError);
  };

  const handleDelete = (lienUid) => {
    AffaireService.getInstance()
      .deleteLien(lienUid)
      .then(r => {
        Toast.success(r.message);
        setLiens(prev => prev.filter(l => l.uid !== lienUid));
      })
      .catch(catchError);
  };

  return (
    <div className={styles.section}>
      <div className={styles.toolbar}>
        <span className={styles.count}>{liens.length}</span>
        <div className={styles.actions}>
          <Button size="small" icon={<ReloadOutlined />} onClick={init} />
        </div>
      </div>

      <Spin spinning={loading}>
        {liens.length === 0 && !loading ? (
          <div style={{ padding: '16px 14px' }}>
            <Empty description="Aucun dossier lié" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : (
          <div className={styles.list}>
            {liens.map(lien => (
              <div key={lien.uid} className={styles.item}>
                <InlineEditableInput
                  canEdit={canManage}
                  type="select"
                  fieldName="type"
                  value={lien.type}
                  record={lien}
                  onChange={handleTypeChange}
                  options={TYPE_OPTIONS_BY_DIRECTION[lien.direction]}
                >
                  <Tag icon={<LinkOutlined />} className={styles.typeTag}>{lien.label}</Tag>
                </InlineEditableInput>
                <NavLink to={`/affaire/${lien.autreAffaire.uid}`} className={styles.lienNom}>
                  [{lien.autreAffaire.code}] {lien.autreAffaire.nom}
                </NavLink>
                {canManage && (
                  <Tooltip title="Supprimer ce lien">
                    <Popconfirm title="Supprimer ce lien ?" okText="Supprimer" cancelText="Annuler" okType="danger" onConfirm={() => handleDelete(lien.uid)}>
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        )}
      </Spin>

      {canManage && (
        <AffaireLienInlineCreator affaire={affaire} onCreated={init} />
      )}
    </div>
  );
}

AffaireLiensList.propTypes = {
  affaire: PropTypes.object.isRequired,
  canManage: PropTypes.bool,
};
